import {useQuery} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import type {PinControls} from '../components/views/PinViewMenuItem'
import type {SavedView} from '../components/views/types'
import {countPinnedViews} from '../components/views/views-sort-options'
import {savedViewsQueryOptions} from '../queries/saved-views'
import {isPinLimitError, pinnedMutationErrorMessage, type SavedViewMutationOptions} from '../utils/saved-view-mutations'
import type {SavedViewPinSource} from './use-saved-view-analytics'
import {useSetSavedViewPinnedMutation} from './use-set-saved-view-pinned-mutation'

export interface SavedViewPinControlsResult {
  pinControls?: PinControls
  pinErrorMessage: string | null
  retryPin?: () => void
  isPinRetrying: boolean
}

export function useSavedViewPinControls({
  owner,
  repo,
  enabled,
  source,
}: SavedViewMutationOptions & {enabled: boolean; source: SavedViewPinSource}): SavedViewPinControlsResult {
  const {mutate, isPending, error, variables} = useSetSavedViewPinnedMutation({owner, repo, source})

  const {data, isFetching} = useQuery({...savedViewsQueryOptions({owner, repo}), enabled})
  const pinnedCount = data?.savedViews ? countPinnedViews(data.savedViews) : undefined
  const pinLimitLoading = enabled && pinnedCount === undefined && isFetching

  const togglePin = useCallback(
    (view: SavedView) => {
      mutate({id: view.id, pinned: !view.pinned})
    },
    [mutate],
  )

  const retryPin = useCallback(() => {
    if (variables) mutate(variables)
  }, [mutate, variables])

  const pinControls = useMemo<PinControls | undefined>(
    () => (enabled ? {togglePin, pinnedCount, pinLimitLoading} : undefined),
    [enabled, togglePin, pinnedCount, pinLimitLoading],
  )

  const canRetry = variables != null && !isPinLimitError(error, variables.pinned)
  const pinErrorMessage = error && variables ? pinnedMutationErrorMessage(error, variables.pinned) : null

  return {pinControls, pinErrorMessage, retryPin: canRetry ? retryPin : undefined, isPinRetrying: isPending}
}
